import React, { useEffect, useState, cloneElement } from 'react';
import { ArrowRight, Cpu, Shield, Globe, Zap, Activity, Lock, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { TypingText } from '../components/TypingText';
const Counter = ({
  end,
  duration = 2000,
  label
}: {
  end: number;
  duration?: number;
  label: string;
}) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime: number;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      setCount(Math.floor(end * percentage));
      if (progress < duration) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  return <div className="text-center group" data-id="element-306">
      <div className="text-4xl md:text-5xl font-display font-bold text-ivory mb-2 group-hover:text-ai-teal transition-colors duration-300" data-id="element-307">
        {count}
        {label.includes('%') ? '%' : '+'}
      </div>
      <div className="text-xs font-mono text-muted-gold uppercase tracking-widest" data-id="element-308">
        {label.replace('%', '')}
      </div>
    </div>;
};
export function Home() {
  const {
    t,
    language
  } = useLanguage();
  const seoTitles = {
    en: 'NEW ASIA TECH - TECHNOLOGY BEYOND CONTROL.',
    id: 'NEW ASIA TECH - TEKNOLOGI MELAMPAUI KENDALI.'
  };
  const seoDescriptions = {
    en: 'We design future technology systems that are intelligent, autonomous, and ethical by design. Sovereign AI infrastructure, predictive governance, and critical asset defense for governments and institutions.',
    id: 'Kami merancang sistem teknologi masa depan yang cerdas, otonom, dan etis sejak awal. Infrastruktur AI berdaulat, tata kelola prediktif, dan pertahanan aset kritis untuk pemerintah dan institusi.'
  };
  const typingTexts = {
    en: 'We design future technology systems that are intelligent, autonomous, and ethical by design.',
    id: 'Kami merancang sistem teknologi masa depan yang cerdas, otonom, dan etis sejak awal.'
  };
  return <>
      <SEO title={seoTitles[language]} description={seoDescriptions[language]} url="/" type="website" data-id="element-309" />

      <div className="min-h-screen pt-20 overflow-hidden" data-id="element-310">
        <section className="relative min-h-[90vh] flex items-center px-6 border-b border-white/5" data-id="element-311">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-neural-green/20 via-transparent to-transparent opacity-50" data-id="element-312" />

          <div className="max-w-[1800px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 relative z-10" data-id="element-313">
            <div className="lg:col-span-12 animate-fade-in-up" data-id="element-314">
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter leading-[0.9] mb-8" data-id="element-315">
                <span className="text-ai-teal" data-id="element-316">TECHNOLOGY</span>{' '}
                <span className="text-ivory" data-id="element-317">BEYOND CONTROL.</span>
              </h1>
              <div className="text-xl md:text-2xl text-muted-gold max-w-2xl font-light tracking-wide border-l-2 border-muted-gold pl-6 py-2 animate-fade-in-up delay-200 min-h-[4rem] flex items-center hacker-text-container" data-id="element-318">
                <TypingText text={typingTexts[language]} speed={40} delay={800} data-id="element-319" />
              </div>

              <div className="mt-12 flex flex-wrap gap-6 animate-fade-in-up delay-300" data-id="element-320">
                <Link to="/solutions" className="group relative px-8 py-4 bg-ivory text-carbon font-bold uppercase tracking-widest overflow-hidden" data-id="element-321">
                  <span className="relative z-10 group-hover:text-ivory transition-colors duration-300" data-id="element-322">
                    {t('home.hero.cta1')}
                  </span>
                  <div className="absolute inset-0 bg-carbon transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" data-id="element-323" />
                </Link>
                <Link to="/contact" className="px-8 py-4 border border-white/20 text-ivory font-bold uppercase tracking-widest hover:bg-white/5 transition-colors duration-300" data-id="element-324">
                  {t('home.hero.cta2')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 border-b border-white/5 bg-white/[0.02]" data-id="element-325">
          <div className="max-w-[1800px] mx-auto px-6" data-id="element-326">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-327">
              <Counter end={15} label={t('home.stats.projects')} data-id="element-328" />
              <Counter end={12} label={t('home.stats.partners')} data-id="element-329" />
              <Counter end={100} label={t('home.stats.sovereignty')} data-id="element-330" />
              <Counter end={24} label={t('home.stats.monitoring')} data-id="element-331" />
            </div>
          </div>
        </section>

        <section className="py-32 px-6 bg-neural-green/5 relative" data-id="element-332">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" data-id="element-333" />
          <div className="max-w-[1800px] mx-auto" data-id="element-334">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-24 items-center" data-id="element-335">
              <div className="animate-fade-in-up" data-id="element-336">
                <h2 className="text-xs font-mono text-muted-gold uppercase tracking-widest mb-6 flex items-center" data-id="element-337">
                  <span className="w-8 h-px bg-muted-gold mr-4" data-id="element-338" />
                  {t('home.manifesto.title')}
                </h2>
                <p className="text-3xl md:text-5xl font-display font-medium leading-tight text-ivory mb-8" data-id="element-339">
                  {t('home.manifesto.heading')}
                </p>
                <p className="text-lg text-gray-400 leading-relaxed mb-8 text-balance" data-id="element-340">
                  {t('home.manifesto.desc')}
                </p>
                <Link to="/philosophy" className="inline-flex items-center text-ai-teal hover:text-white transition-colors uppercase tracking-widest text-sm font-bold group" data-id="element-341">
                  {t('home.manifesto.cta')}{' '}
                  <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" data-id="element-342" />
                </Link>
              </div>

              <div className="relative h-[500px] border border-white/10 bg-carbon/40 p-1 backdrop-blur-sm animate-fade-in-up delay-200 group" data-id="element-343">
                <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-transparent" data-id="element-344" />
                <div className="relative z-10 grid grid-cols-2 gap-px bg-white/10 w-full h-full border border-white/5" data-id="element-345">
                  {[{
                  icon: <Cpu data-id="element-346" />,
                  label: 'COMPUTATIONAL INTELLIGENCE'
                }, {
                  icon: <Shield data-id="element-347" />,
                  label: 'SOVEREIGN SECURITY'
                }, {
                  icon: <Globe data-id="element-348" />,
                  label: 'GLOBAL INFRASTRUCTURE'
                }, {
                  icon: <Zap data-id="element-349" />,
                  label: 'AUTONOMOUS SYSTEMS'
                }].map((item, idx) => <div key={idx} className="bg-carbon/80 p-6 flex flex-col justify-between hover:bg-white/5 transition-colors duration-300 group/item" data-id="element-350">
                      {cloneElement(item.icon as React.ReactElement, {
                    className: 'text-muted-gold h-8 w-8 mb-4 group-hover/item:text-ai-teal transition-colors duration-300'
                  })}
                      <span className="font-mono text-xs text-gray-400 tracking-wider" data-id="element-351">
                        {item.label}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-32 px-6 relative overflow-hidden" data-id="element-352">
          <div className="absolute inset-0 bg-tech-grid opacity-20" data-id="element-353" />
          <div className="max-w-[1800px] mx-auto relative z-10" data-id="element-354">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 border-b border-white/10 pb-8" data-id="element-355">
              <h2 className="text-4xl md:text-6xl font-display font-bold text-ivory" data-id="element-356">
                {t('home.domains.title')}
              </h2>
              <span className="text-xs font-mono text-graphite mt-4 md:mt-0 flex items-center" data-id="element-357">
                <Activity className="w-4 h-4 mr-2 text-ai-teal" data-id="element-358" />
                {t('home.domains.subtitle')}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-359">
              {[{
              title: t('home.domains.ai.title'),
              desc: t('home.domains.ai.desc'),
              id: '01',
              icon: <Cpu className="w-12 h-12 mb-6 text-muted-gold" data-id="element-360" />
            }, {
              title: t('home.domains.cyber.title'),
              desc: t('home.domains.cyber.desc'),
              id: '02',
              icon: <Server className="w-12 h-12 mb-6 text-ai-teal" data-id="element-361" />
            }, {
              title: t('home.domains.defense.title'),
              desc: t('home.domains.defense.desc'),
              id: '03',
              icon: <Lock className="w-12 h-12 mb-6 text-neural-green" data-id="element-362" />
            }].map((item, idx) => <div key={item.id} className={`bg-carbon border border-white/10 p-12 group hover:border-ai-teal/30 hover:shadow-[0_0_30px_-10px_rgba(62,240,209,0.1)] transition-all duration-500 animate-fade-in-up delay-${idx * 100 + 100}`} data-id="element-363">
                  <div className="flex justify-between items-start mb-8" data-id="element-364">
                    {item.icon}
                    <div className="text-xs font-mono text-white/20" data-id="element-365">
                      {item.id}
                    </div>
                  </div>
                  <h3 className="text-2xl font-display font-bold text-ivory mb-4 group-hover:text-ai-teal transition-colors" data-id="element-366">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors" data-id="element-367">
                    {item.desc}
                  </p>
                  <div className="mt-8 w-full h-px bg-white/10 group-hover:bg-ai-teal/50 transition-colors duration-500 origin-left transform scale-x-50 group-hover:scale-x-100" data-id="element-368" />
                </div>)}
            </div>
          </div>
        </section>

        <section className="py-40 px-6 border-t border-white/5 relative" data-id="element-369">
          <div className="absolute inset-0 bg-gradient-to-b from-carbon to-neural-green/10" data-id="element-370" />
          <div className="max-w-[1800px] mx-auto text-center relative z-10" data-id="element-371">
            <h2 className="text-4xl md:text-7xl font-display font-bold text-ivory mb-8 tracking-tight" data-id="element-372">
              {t('home.cta.title')}
            </h2>
            <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-373">
              {t('home.cta.desc')}
            </p>
            <Link to="/contact" className="inline-block border border-white/20 px-12 py-4 text-sm font-bold uppercase tracking-[0.2em] text-ivory hover:bg-white hover:text-carbon transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,255,255,0.2)]" data-id="element-374">
              {t('home.cta.button')}
            </Link>
          </div>
        </section>
      </div>
    </>;
}